import React, { useCallback, useEffect, useState } from 'react'
import { Button, Form, Input, InputNumber, Switch, Typography, message, Space, Card } from 'antd'
import B2BLayout from '../../../layouts/b2b'
import { getPlatformSetting, updatePlatformSetting, testPlatformMail } from '../api'

const { Title, Text } = Typography

type MailFormValues = {
  from_email?: string
  from_name?: string
  password?: string
  smtp_host?: string
  smtp_port?: number
  smtp_secure?: boolean
  is_enabled?: boolean
  test_to?: string
}

export default function PlatformSettingsPage() {
  const [form] = Form.useForm<MailFormValues>()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [hasPassword, setHasPassword] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const result = await getPlatformSetting('mail')
      const value = result?.data?.value || {}
      setHasPassword(Boolean(value.has_password))
      form.setFieldsValue({
        from_email: value.from_email || '',
        from_name: value.from_name || '',
        password: undefined,
        smtp_host: value.smtp_host || '',
        smtp_port: value.smtp_port != null ? Number(value.smtp_port) : 587,
        smtp_secure: Boolean(value.smtp_secure),
        is_enabled: value.is_enabled !== false,
      })
    } catch (e: any) {
      message.error(e?.message || 'Не удалось загрузить настройки')
    } finally {
      setLoading(false)
    }
  }, [form])

  useEffect(() => {
    load()
  }, [load])

  const onSave = async () => {
    try {
      const values = await form.validateFields([
        'from_email',
        'from_name',
        'password',
        'smtp_host',
        'smtp_port',
        'smtp_secure',
        'is_enabled',
      ])
      setSaving(true)
      const payload: Record<string, unknown> = {
        from_email: values.from_email,
        from_name: values.from_name || '',
        smtp_host: values.smtp_host,
        smtp_port: values.smtp_port,
        smtp_secure: Boolean(values.smtp_secure),
        is_enabled: values.is_enabled !== false,
      }
      if (values.password) payload.password = values.password

      const result = await updatePlatformSetting('mail', payload)
      setHasPassword(Boolean(result?.data?.value?.has_password))
      form.setFieldValue('password', undefined)
      message.success('Настройки почты сохранены')
    } catch (e: any) {
      if (e?.errorFields) return
      message.error(e?.message || 'Ошибка сохранения')
    } finally {
      setSaving(false)
    }
  }

  const onTest = async () => {
    const to = String(form.getFieldValue('test_to') || '').trim()
    if (!to) {
      message.warning('Укажите адрес для тестового письма')
      return
    }
    setTesting(true)
    try {
      await testPlatformMail(to)
      message.success('Тестовое письмо отправлено')
    } catch (e: any) {
      message.error(e?.message || 'Не удалось отправить тестовое письмо')
    } finally {
      setTesting(false)
    }
  }

  const breadcrumbs = [
    { href: '/admin/dealers/domains', title: 'Поддомены дилеров' },
    { href: '/admin/dealers/settings', title: 'Настройки' },
  ]

  return (
    <B2BLayout breadcrumbs={breadcrumbs} pageTitle="Настройки платформы">
      <Space direction="vertical" style={{ width: '100%' }} size={16}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Почта
          </Title>
          <Text type="secondary">
            SMTP для платформенных писем (регистрация, оплата, биллинг и т.п.)
          </Text>
        </div>

        <Card loading={loading}>
          <Form form={form} layout="vertical" style={{ maxWidth: 560 }}>
            <Form.Item name="is_enabled" label="Включено" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item
              name="from_email"
              label="Отправитель (email)"
              rules={[{ required: true, message: 'Укажите email' }, { type: 'email' }]}
            >
              <Input placeholder="noreply@zeiqservice.ru" />
            </Form.Item>
            <Form.Item name="from_name" label="Отправитель (имя)">
              <Input placeholder="ZE.IQ Service" />
            </Form.Item>
            <Form.Item
              name="smtp_host"
              label="SMTP хост"
              rules={[{ required: true, message: 'Укажите SMTP хост' }]}
            >
              <Input placeholder="mail.zeiqservice.ru" />
            </Form.Item>
            <Form.Item
              name="smtp_port"
              label="SMTP порт"
              rules={[{ required: true, message: 'Укажите SMTP порт' }]}
            >
              <InputNumber min={1} max={65535} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="smtp_secure" label="SMTPS (secure)" valuePropName="checked">
              <Switch checkedChildren="ВКЛЮЧЕНО (465)" unCheckedChildren="ВЫКЛЮЧЕНО (587)" />
            </Form.Item>
            <Form.Item
              name="password"
              label={hasPassword ? 'Пароль (оставьте пустым, чтобы не менять)' : 'Пароль'}
              rules={hasPassword ? [] : [{ required: true, message: 'Укажите пароль' }]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>

            <Space>
              <Button type="primary" onClick={onSave} loading={saving}>
                Сохранить
              </Button>
            </Space>

            <Title level={5} style={{ marginTop: 32 }}>
              Тест
            </Title>
            <Form.Item name="test_to" label="Отправить на">
              <Input placeholder="you@example.com" />
            </Form.Item>
            <Button onClick={onTest} loading={testing}>
              Отправить тестовое письмо
            </Button>
          </Form>
        </Card>
      </Space>
    </B2BLayout>
  )
}
